import { useEffect, useRef, useState } from 'react';

interface Props {
  /** Something identifying THIS celebration (the order id). The burst plays
   *  once per id, remembered at module level — so parent re-renders and even
   *  full screen remounts (processing → received → delivered) can't replay
   *  it, and crucially can't cancel a burst that is mid-flight. This memory
   *  used to live in the parent's render condition, which unmounted the
   *  canvas on the first re-render after mount — the "no confetti" bug. */
  playId?: string;
  pieces?: number;
  durationMs?: number;
}

/** Order ids that already celebrated, surviving component remounts. Resets on
 *  page reload — which is fine: the banner doesn't survive reload either. */
const playedIds = new Set<string>();

/** Test-only: clear the module-level memory between cases. */
export function __resetConfettiMemoryForTests() {
  playedIds.clear();
}

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

const COLORS = ['#ffd75a', '#ffffff', '#7fe3a8', '#7aa5f8', '#ff8fa3'];

/**
 * One celebratory confetti burst over the whole viewport (fixed overlay,
 * pointer-events: none), self-removing when the last piece falls. Hand-rolled
 * canvas — no library. Reduced-motion fans get nothing (the golden banner
 * carries the message on its own).
 */
export function ConfettiBurst({ playId, pieces = 80, durationMs = 4500 }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [done, setDone] = useState(
    () => prefersReducedMotion() || (!!playId && playedIds.has(playId)),
  );

  useEffect(() => {
    if (done) return;
    if (playId) playedIds.add(playId);

    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) {
      setDone(true);
      return;
    }

    const W = (canvas.width = window.innerWidth);
    const H = (canvas.height = window.innerHeight);
    const ps = Array.from({ length: pieces }, () => ({
      x: Math.random() * W,
      y: -20 - Math.random() * H * 0.5,
      vx: (Math.random() - 0.5) * 1.6,
      vy: 1.5 + Math.random() * 2.5,
      w: 5 + Math.random() * 4,
      h: 8 + Math.random() * 6,
      r: Math.random() * Math.PI,
      vr: (Math.random() - 0.5) * 0.25,
      c: COLORS[Math.floor(Math.random() * COLORS.length)],
    }));

    let raf = 0;
    const t0 = performance.now();
    const frame = (t: number) => {
      ctx.clearRect(0, 0, W, H);
      let alive = false;
      for (const p of ps) {
        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.02;
        p.r += p.vr;
        if (p.y < H + 20) alive = true;
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.r);
        ctx.fillStyle = p.c;
        ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        ctx.restore();
      }
      if (alive && t - t0 < durationMs) {
        raf = requestAnimationFrame(frame);
      } else {
        setDone(true);
      }
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [done]);

  if (done) return null;

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className="fixed inset-0 z-40 pointer-events-none"
    />
  );
}
